import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
    Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Flex, Icon, Stack, Text, useColorModeValue,
} from "@chakra-ui/react";
import { MdSync } from "react-icons/md";
import Card from "horizon/components/card/Card";
import MigrationConfirmDialog from "./MigrationConfirmDialog";

/**
 * Executes the migration API call and handles result/error.
 * @param {Function} setMigrating - Migration loading state setter
 * @param {Function} setMigrationResult - Migration result state setter
 * @param {Function|null} onMigrationComplete - Callback after successful migration
 * @param {Function} setOpenModal - Modal open state setter
 * @returns {Promise<void>}
 */
async function executeMigration(setMigrating, setMigrationResult, onMigrationComplete, setOpenModal) {
    setMigrating(true);
    setMigrationResult(null);
    try {
        const response = await axios.get('/service_search/debug/fix-and-migrate');
        setMigrationResult(response.data);
        toast.success("Migration terminée avec succès !");
        setTimeout(() => {
            if (onMigrationComplete) onMigrationComplete();
            setOpenModal(false);
        }, 2000);
    } catch (error) {
        console.error("Migration error:", error);
        toast.error("Erreur lors de la migration");
        setMigrationResult({ error: error.response?.data?.error || "Erreur inconnue" });
    } finally {
        setMigrating(false);
    }
}

/**
 * Closes the migration modal and resets the result.
 * @param {Function} setOpenModal - Modal open state setter
 * @param {Function} setMigrationResult - Migration result state setter
 */
function dismissMigration(setOpenModal, setMigrationResult) {
    setOpenModal(false);
    setMigrationResult(null);
}

/**
 * Card header with sync icon badge and title.
 * @returns {React.ReactElement}
 */
function CardHeader() {
    const badgeBg = useColorModeValue("brand.100", "whiteAlpha.100");
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Flex align="center" gap="12px" mb="16px">
            <Flex align="center" justify="center" w="44px" h="44px" borderRadius="12px" bg={badgeBg}>
                <Icon as={MdSync} w="24px" h="24px" color="brand.500" />
            </Flex>
            <Box>
                <Text fontSize="lg" fontWeight="700" color={titleColor}>Migration et réindexation</Text>
                <Text fontSize="sm" color={subColor}>Synchroniser la base de données avec ElasticSearch</Text>
            </Box>
        </Flex>
    );
}

/**
 * Warning alert shown when migration is needed.
 * @param {object} props
 * @param {number} props.missing - Number of services not indexed
 * @returns {React.ReactElement}
 */
function MigrationAlert({ missing }) {
    return (
        <Alert status="warning" borderRadius="12px" alignItems="flex-start">
            <AlertIcon />
            <Box>
                <AlertTitle fontSize="sm">Migration recommandée</AlertTitle>
                <AlertDescription fontSize="xs">{missing} service(s) ne sont pas indexés</AlertDescription>
            </Box>
        </Alert>
    );
}

/**
 * Single stat row with label and colored value.
 * @param {object} props
 * @param {string} props.label - Row label
 * @param {number} props.value - Row value
 * @param {string} [props.color] - Optional color token for the value
 * @returns {React.ReactElement}
 */
function StatRow({ label, value, color }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const valColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Flex justify="space-between" align="center">
            <Text fontSize="sm" color={labelColor}>{label}</Text>
            <Text fontSize="md" fontWeight="700" color={color || valColor}>{value}</Text>
        </Flex>
    );
}

/**
 * Statistics box with service counts.
 * @param {object} props
 * @param {number} props.totalServices - Total DB services count
 * @param {number} props.indexedServices - Indexed services count
 * @param {boolean} props.needsMigration - Whether migration is needed
 * @returns {React.ReactElement}
 */
function MigrationStats({ totalServices, indexedServices, needsMigration }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    return (
        <Box p="14px" bg={bg} borderRadius="12px">
            <Stack spacing="10px">
                <StatRow label="Services en base de données" value={totalServices} />
                <StatRow label="Services indexés" value={indexedServices} color={needsMigration ? "orange.500" : "green.500"} />
                {needsMigration && <StatRow label="Services manquants" value={totalServices - indexedServices} color="red.500" />}
            </Stack>
        </Box>
    );
}

/**
 * Renders the migration card body with header, alert, stats, and button.
 * @param {object} props
 * @param {boolean} props.needsMigration - Whether migration is needed
 * @param {number} props.totalServices - Total DB services count
 * @param {number} props.indexedServices - Indexed services count
 * @param {Function} props.onOpenModal - Opens the migration confirm dialog
 * @returns {React.ReactElement}
 */
function MigrationCardContent({ needsMigration, totalServices, indexedServices, onOpenModal }) {
    return (
        <Card>
            <CardHeader />
            <Stack spacing="16px">
                {needsMigration && <MigrationAlert missing={totalServices - indexedServices} />}
                <MigrationStats totalServices={totalServices} indexedServices={indexedServices} needsMigration={needsMigration} />
                <Button w="100%" colorScheme="brand" leftIcon={<Icon as={MdSync} />} onClick={onOpenModal}>
                    Lancer la migration
                </Button>
            </Stack>
        </Card>
    );
}

/**
 * Card component for managing ElasticSearch data migration and reindexing.
 * @param {object} props
 * @param {object} props.statusData - Current ES and database status data
 * @param {Function} props.onMigrationComplete - Callback after successful migration
 * @returns {React.ReactElement}
 */
function ElasticMigrationCard({ statusData, onMigrationComplete }) {
    const [openModal, setOpenModal] = useState(false);
    const [migrating, setMigrating] = useState(false);
    const [migrationResult, setMigrationResult] = useState(null);
    const totalServices = statusData?.database?.services_count || 0;
    const indexedServices = statusData?.elasticsearch?.optimized_document_count || 0;
    const needsMigration = totalServices !== indexedServices;

    return (
        <>
            <MigrationCardContent needsMigration={needsMigration} totalServices={totalServices}
                indexedServices={indexedServices} onOpenModal={() => setOpenModal(true)} />
            <MigrationConfirmDialog open={openModal} onClose={() => setOpenModal(false)}
                onConfirm={() => executeMigration(setMigrating, setMigrationResult, onMigrationComplete, setOpenModal)}
                migrating={migrating} migrationResult={migrationResult} totalServices={totalServices}
                onDismissResult={() => dismissMigration(setOpenModal, setMigrationResult)} />
        </>
    );
}

export default ElasticMigrationCard;
